import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useConnection, useConnectionSummaries } from '@/entities/connection';
import { usePlacements } from '@/features/federation';
import {
  CLASSIFY_DATASETS_PERMISSION,
  CONFIGURE_HARVEST_SCHEDULE_PERMISSION,
  DELETE_SOURCE_PERMISSION,
  DISABLE_SOURCE_PERMISSION,
  PROFILE_DATASETS_PERMISSION,
  ROTATE_CREDENTIAL_PERMISSION,
  can,
  useIdentity,
} from '@/entities/session';

import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { TextField } from '@/shared/ui/Field';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { HelpLabel, InfoTip } from '@/shared/ui/InfoTip';
import { Sections } from '@/shared/ui/Layout';
import { OverflowMenu } from '@/shared/ui/OverflowMenu';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { Tabs } from '@/shared/ui/Tabs';

import {
  datasetsBlockingDelete,
  useDeleteConnection,
  useHarvest,
  useReplaceCredential,
  useRequestHarvest,
  useSchemaPreview,
  useSetInService,
  useVerifyConnection,
} from '../api/connections';
import { useConnectors } from '../api/connectors';
import { useReadiness } from '@/features/platform-status';
import {
  HarvestScheduleCard,
  isDurable,
  occurrenceLabel,
  presenceOf,
  schedulerHealth,
  useHarvestSchedule,
} from '@/features/scheduling';
import { AutoProfileCard } from './AutoProfileCard';
import { ConnectorTile } from './ConnectorTile';
import { HarvestProgress } from './HarvestProgress';
import { ReplaceCredentialDialog } from './ReplaceCredentialDialog';
import { SourceActivity } from './SourceActivity';
import { SourceDatasetsTab } from './SourceDatasetsTab';

import { CAPABILITIES, capabilitiesOf, reachOf } from '../model/capabilities';
import { departureState } from '../model/listing';
import { arrivalFrom } from '../model/register';
import { schemasOf, shareOf } from '../model/sourceDatasets';
import { useSourceDatasets } from '../api/sourceDatasets';
import {
  ageSince,
  blocksAnotherHarvest,
  harvestReading,
  lastChecked,
  resumableHarvest,
} from '../model/listing';
import { CONNECTOR_LABEL, credentialFacts } from '../model/source';
import { versionReading, versionSentence, versionText } from '../model/version';
import type { Connection } from '@/shared/api/generated/catalog';
import {
  CONNECTIONS_QUERY_KEY,
  readyToHarvest,
  tracksRenames,
} from '@/entities/connection';

/* One source: what it is, what this deployment can do with it, what has happened to it, and every
 * action that acts on it.
 *
 * **The actions moved here from the list, and that is the point of the redesign.** Verify, Harvest,
 * Edit credential, Disable and Delete are decisions about *one* source; a grid of cards put five sets
 * of them on one screen, which meant a destructive control sat inside a scroll region next to four
 * others and a page-level failure banner could not say which card it belonged to. The list is now a
 * worklist that ranks and links; this is where a reader acts.
 *
 * **Read-only was the old rule here and it was the wrong one.** The previous version said adding
 * these buttons "would be a second place `SourceCard`'s mutation logic has to be kept in sync" —
 * true while both existed, and the answer is that only one does now.
 *
 * **The datasets section is `CatalogBrowser` itself, scoped rather than reimplemented.** The search
 * box, the PII and score columns and every state they distinguish come from the one table this
 * platform already has for datasets; a second table here would be a second place those distinctions
 * can drift. `key={connectionId}` forces a clean remount when navigating from one source's page
 * straight to another's, so a search term or a pager cursor signed for source A's query can never be
 * carried into source B's.
 */

const STATE_TONE = {
  verified: 'ok',
  failed: 'error',
  registered: 'unknown',
  disabled: 'unknown',
} as const satisfies Record<Connection['state'], string>;

/** The states that report no measurement wear a hollow dot, as on the cards. */
const HOLLOW: Readonly<Record<Connection['state'], boolean>> = {
  verified: false,
  failed: false,
  registered: true,
  disabled: true,
};

type DetailTab = 'datasets' | 'automation' | 'activity' | 'details';

function Breadcrumbs({ name }: { readonly name: string }): ReactElement {
  const { t } = useTranslation();
  return (
    <nav aria-label={t('breadcrumbs.label')} className="lq-breadcrumbs">
      <Link to={SCREEN_PATHS.connections}>{t('nav.connections')}</Link>
      <span aria-hidden="true" className="lq-breadcrumbs__sep">
        ›
      </span>
      {/* `aria-current="page"` rather than a link to here, the same choice `DatasetDetail`'s
          breadcrumb makes: a last crumb that navigates to the page you are on costs a screen-reader
          user a round trip for nothing. */}
      <span aria-current="page">{name}</span>
    </nav>
  );
}

export function ConnectionDetail(): ReactElement {
  const { t } = useTranslation();
  const { connectionId = '' } = useParams<{ connectionId: string }>();
  /* Set by the register dialog when it lands here, so a person who just registered the source is
     told what happened — registered, and whether its first harvest started — on the page it opened. */
  const arrival = arrivalFrom(useLocation().state);
  const connection = useConnection(connectionId);
  const connectors = useConnectors();
  const placements = usePlacements();
  const summaries = useConnectionSummaries();
  const identity = useIdentity();

  const verify = useVerifyConnection();
  const harvest = useRequestHarvest();
  /* **Whether a scheduled harvest survives a restart, read once for the page.**
   *
   * `/readiness` reports only the *degraded* capabilities, so an absent `harvest_scheduler` means
   * it is answering — which is why this goes through `schedulerHealth` rather than looking for a
   * `production` entry that is never sent. A failed read yields `unknown`, which is not durable:
   * a card must not promise durability this screen could not verify. */
  const readiness = useReadiness();
  const durable = isDurable(
    schedulerHealth(readiness.isSuccess ? readiness.data.degraded : null),
    'harvest_scheduler',
  );
  const service = useSetInService();
  const credential = useReplaceCredential();
  const forget = useDeleteConnection();
  const navigate = useNavigate();
  const schedule = useHarvestSchedule(connectionId);
  /* Shared with the Datasets tab by query key, so the Details tab's schema list costs no request of
     its own and the two cannot disagree about what this source holds. */
  const sourceDatasets = useSourceDatasets(connectionId);
  /* The two confirmations the design asks for. Out of service is reversible and still asks, because
     it stops harvests and rule runs a team may be relying on; delete asks for the name typed. */
  const [askingService, setAskingService] = useState(false);
  const [typedName, setTypedName] = useState('');

  const client = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  /* Awaited so the button's busy state covers the actual refetch rather than only the invalidation,
     which returns immediately and would make the control flicker and settle before anything new had
     arrived — a spinner that lies about being finished. */
  const refresh = async (): Promise<void> => {
    setRefreshing(true);
    try {
      await Promise.all([
        client.invalidateQueries({ queryKey: CONNECTIONS_QUERY_KEY }),
        /* The harvest's own effects: the dataset listing this page embeds, the summaries that carry
           the dataset count and last-harvested time, and the audit rows the activity panel reads. */
        client.invalidateQueries({ queryKey: ['catalog'] }),
        client.invalidateQueries({ queryKey: ['audit'] }),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const [watching, setWatching] = useState<string | null>(null);
  /* The run whose card was dismissed, so a summary that still reports it as running — it is read once
     and not refetched — cannot bring the card straight back. */
  const [dismissed, setDismissed] = useState<string | null>(null);

  /* **A run already in flight when the page loads is picked up rather than lost.** The card used to
     exist only for the harvest this page had just requested, so a reload, or arriving from the list
     while a scheduled harvest ran, showed nothing but a sentence above the datasets. The summary
     names the most recent run; while that run is queued or running it is adopted as the one being
     watched.

     Adopted into `watching` during render rather than derived each time, and that is deliberate:
     when the run finishes the summaries are refetched, the summary stops calling it running, and a
     derived value would make the card vanish in the instant it turned to "Completed". Latching it
     keeps the outcome on screen until somebody dismisses it. Setting state while rendering is React's
     documented pattern for adjusting to a changed input, and the guard makes it settle in one pass. */
  const resumable = resumableHarvest(
    summaries.data?.items.find((s) => s.connection_id === connectionId),
  );
  if (watching === null && resumable !== null && resumable !== dismissed) {
    setWatching(resumable);
  }

  /* Read here as well as inside the card so the Harvest button can see it. One query key, so TanStack
     shares the polling rather than issuing a second request. */
  const watched = useHarvest(watching);

  /* When a run finishes, the datasets it wrote, the summary that carries the count, and the activity
     rows are all stale — the same keys Refresh invalidates, without its busy state, because nobody
     pressed anything. */
  const onHarvestFinished = useCallback(() => {
    void Promise.all([
      client.invalidateQueries({ queryKey: CONNECTIONS_QUERY_KEY }),
      client.invalidateQueries({ queryKey: ['catalog'] }),
      client.invalidateQueries({ queryKey: ['audit'] }),
    ]);
  }, [client]);
  const [armed, setArmed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<DetailTab>('datasets');
  /* The schema listing reaches the customer's source, so it runs only when somebody asks — and
     only on a source with nothing harvested, where the catalogue has no schemas to show. */
  const [askedSchemas, setAskedSchemas] = useState(false);
  const schemaPreview = useSchemaPreview(askedSchemas ? connectionId : null);

  /* One instant for the whole render, so the status line and the last-checked line cannot differ by
   * a minute — two `new Date()` calls can report one timestamp as "59 minutes" and "1 hour". Not
   * memoised: the factory reads nothing from the query, so a dependency array would be a claim
   * eslint correctly refused, and a plain const is already once per render. */
  const now = new Date();

  if (connection.isPending) {
    return (
      <>
        <p role="status" className="lq-muted">
          {t('connectionDetail.loading')}
        </p>
        <SkeletonRows rows={4} columns={2} />
      </>
    );
  }

  if (connection.isError) {
    return (
      <>
        <Alert tone="error" title={t('connectionDetail.unreachable')}>
          {connection.error.message}
        </Alert>
        <p className="lq-source__note">
          <Link to={SCREEN_PATHS.connections}>
            {t('connectionDetail.backToConnections')}
          </Link>
        </p>
      </>
    );
  }

  /* A successful read that found nothing under this id — a bad link, a retired source, or (at up to
   * 200 connections) one past the list's cap. Not the same as `isError`: the request worked and
   * answered honestly that this id is not among the connections it returned. */
  if (connection.data === undefined) {
    /* **Deleted from this page, and on its way to the list.** The delete drops the row from the
       shared cache, so for the instant before the navigation lands this page has nothing to show —
       and "not found" would be a false account of a source the person has just retired. */
    if (forget.isSuccess) {
      return <SkeletonRows rows={4} columns={2} />;
    }
    return (
      <>
        <Alert tone="notice" title={t('connectionDetail.notFoundTitle')}>
          {t('connectionDetail.notFoundGuidance')}
        </Alert>
        <p className="lq-source__note">
          <Link to={SCREEN_PATHS.connections}>
            {t('connectionDetail.backToConnections')}
          </Link>
        </p>
      </>
    );
  }

  const source = connection.data;
  const support = capabilitiesOf(source, connectors.data?.items ?? []);
  const reach = reachOf(support);
  const profile = connectors.data?.items.find(
    (entry) => entry.connector_type === source.connector_type,
  );
  const inService = source.state !== 'disabled';
  const credentials = credentialFacts(source);
  const checked = lastChecked(source, now);
  const version = versionReading(source.source_version);
  /* A second harvest is refused from here while one is outstanding. The server refuses it too, with
     a 409 — but for a *queued* run it does the opposite: a never-started run is judged abandoned the
     moment another request arrives and is replaced, so a double-click would discard the first job
     rather than being refused. */
  const harvestBusy = blocksAnotherHarvest(watched.data, now);
  const running = harvestReading(
    summaries.data?.items.find((s) => s.connection_id === source.id),
  );

  const region =
    placements.data?.find((p) => p.connection_id === source.id)?.region_code ?? null;

  /* How many datasets the server's last refusal said are in the way, or `null`. Derived from the
     mutation state rather than held in a `useState`, so it cannot survive a `reset()` and leave a
     stale count armed on a dialog the operator reopened. */
  const blocking = datasetsBlockingDelete(forget.error);

  const unreachable =
    verify.data !== undefined && !verify.data.reachable ? verify.data : null;
  const actionFailure =
    (verify.error ?? harvest.error ?? service.error ?? forget.error)?.message ?? null;

  /* `can` hides controls and protects nothing — every route refuses server-side regardless — and it
     exists so a reader is not offered a door that will slam. Both of these reach `admin` only. */
  const mayDisable = can(identity.data, DISABLE_SOURCE_PERMISSION);
  const mayRotate = can(identity.data, ROTATE_CREDENTIAL_PERMISSION);
  const mayDelete = can(identity.data, DELETE_SOURCE_PERMISSION);

  const summary = summaries.data?.items.find((s) => s.connection_id === source.id);
  const harvestAge = ageSince(summary?.last_harvest_at ?? null, now);
  const datasetCount = summary === undefined ? null : summary.datasets;

  /* The design's four figure tiles: datasets, how many are profiled, how many have their PII
     reviewed, and the last harvest with what the schedule will do next. The two coverage figures are
     the summary's own counts; an unread summary renders a dash and words, never a zero. */
  const profiledCount = summary === undefined ? null : summary.profiled;
  const reviewedCount = summary === undefined ? null : summary.pii_reviewed;
  const coverage = (part: number | null): { value: string; sub: string } => {
    if (part === null || datasetCount === null) return { value: '—', sub: '' };
    /* A dash and nothing under it, as the design has it: the Datasets tile beside it already says
       nothing has been harvested, and saying it three times reads as three problems. */
    if (datasetCount === 0) return { value: '—', sub: '' };
    const share = shareOf(part, datasetCount);
    return {
      value: t('connectionDetail.tile.ofTotal', { part, total: datasetCount }),
      sub:
        share === null
          ? ''
          : t('connectionDetail.tile.percentOfTables', { percent: share }),
    };
  };
  const presence = schedule.isSuccess ? presenceOf(schedule.data) : null;
  /* In the schedule's own zone and the schedule card's own words, so the tile and the card on the
     same page cannot print one occurrence two ways. */
  const nextRun = schedule.isSuccess
    ? occurrenceLabel(schedule.data.next_run_at, schedule.data.timezone)
    : null;
  const scheduleSub =
    presence === null
      ? ''
      : presence.kind === 'armed' && nextRun !== null
        ? t('connectionDetail.tile.nextHarvest', { when: nextRun })
        : t(`connectionDetail.tile.schedule.${presence.kind}`);

  const profiledTile = coverage(profiledCount);
  const reviewedTile = coverage(reviewedCount);
  const tiles = [
    {
      id: 'datasets',
      tone: 'blue',
      label: t('connectionDetail.tile.datasets'),
      help: t('help.metric.datasetsHarvested'),
      value: datasetCount === null ? '—' : datasetCount.toLocaleString(),
      sub:
        datasetCount === null
          ? t('connectionDetail.tile.unread')
          : datasetCount === 0
            ? t('connectionDetail.tile.nothingHarvested')
            : t('connectionDetail.tile.harvested'),
    },
    {
      id: 'profiled',
      tone: 'mint',
      label: t('connectionDetail.tile.profiled'),
      help: t('help.metric.profiled'),
      value: profiledTile.value,
      sub: profiledTile.sub,
    },
    {
      id: 'reviewed',
      tone: 'lavender',
      label: t('connectionDetail.tile.piiReviewed'),
      help: t('help.metric.piiReviewed'),
      value: reviewedTile.value,
      sub: reviewedTile.sub,
    },
    {
      id: 'harvest',
      tone: 'amber',
      label: t('connectionDetail.tile.lastHarvest'),
      help: t('help.feature.harvest'),
      /* Not `checkedSentence`, whose never-case says "Never verified": a source never harvested
         has not been harvested, which is a different absence. */
      value:
        harvestAge.kind === 'never'
          ? t('connectionDetail.tile.never')
          : checkedSentence(t, harvestAge),
      sub:
        running.kind === 'running' || running.kind === 'starting'
          ? t('connectionDetail.tile.harvestRunning')
          : running.kind === 'failed'
            ? t('connectionDetail.tile.harvestFailed')
            : running.kind === 'partial'
              ? t('connectionDetail.tile.harvestPartial')
              : scheduleSub !== ''
                ? scheduleSub
                : harvestAge.kind === 'never'
                  ? t('connectionDetail.tile.neverHarvested')
                  : t('connectionDetail.tile.harvestDone'),
    },
  ] as const;

  const menuItems = [
    {
      id: 'refresh',
      label: t('connectionDetail.refresh'),
      onSelect: () => {
        void refresh();
      },
      disabled: refreshing,
    },
    ...(mayRotate
      ? [
          {
            id: 'credential',
            label: t('connectionDetail.credential.open'),
            onSelect: () => {
              setEditing(true);
            },
          },
        ]
      : []),
    ...(mayDisable
      ? [
          {
            id: 'service',
            label: inService
              ? t('connectionDetail.disable')
              : t('connectionDetail.enable'),
            onSelect: () => {
              setAskingService(true);
            },
            disabled: service.isPending,
          },
        ]
      : []),
    ...(mayDelete
      ? [
          {
            id: 'delete',
            label: t('connectionDetail.deleteSource'),
            danger: true,
            onSelect: () => {
              setTypedName('');
              setArmed(true);
            },
          },
        ]
      : []),
  ];

  return (
    <Sections>
      <Breadcrumbs name={source.name} />

      {arrival !== null && arrival.name === source.name ? (
        <Alert tone="notice" title={t('connections.register.registeredTitle')}>
          {arrival.harvesting
            ? t('connections.register.justRegisteredHarvesting', { name: arrival.name })
            : t('connections.register.justRegisteredLater', { name: arrival.name })}
        </Alert>
      ) : null}

      <div className="lq-cd__head">
        <ConnectorTile type={source.connector_type} />
        <div className="lq-cd__identity">
          <div className="lq-cd__titleRow">
            <h1 className="lq-cd__name">{source.name}</h1>
            <span
              className="lq-cn__state"
              data-tone={STATE_TONE[source.state]}
              title={t(`connections.state.${source.state}.meaning`)}
            >
              <span
                className={`lq-cn__dot${HOLLOW[source.state] ? ' lq-cn__dot--hollow' : ''}`}
                aria-hidden="true"
              />
              {t(`connections.card.state.${source.state}`)}
            </span>
          </div>
          <div className="lq-cd__sub">
            <span className="lq-cd__type">{CONNECTOR_LABEL[source.connector_type]}</span>
            <span className="lq-cd__bullet" aria-hidden="true">
              •
            </span>
            <span className="lq-cd__address">
              {`${source.host}:${String(source.port)} / ${source.database}`}
            </span>
          </div>
        </div>
        <div className="lq-cd__actions">
          <Button
            busy={verify.isPending}
            onClick={() => {
              verify.mutate(source.id);
            }}
          >
            {verify.isPending ? t('connections.card.verifying') : t('connections.verify')}
          </Button>
          {/* Beside the control rather than on it: what verifying does to a source is what a reader
              deciding whether to press it needs, and the button's own label changes while it runs. */}
          <InfoTip subject={t('connections.verify')} text={t('help.action.verify')} />
          <Button
            tone="primary"
            busy={harvest.isPending}
            /* Harvest stays gated on a verified source, with the reason on the control. Queueing a
               harvest against untested credentials fails minutes later and asynchronously, where the
               failure is much harder to connect back to its cause. */
            disabled={!readyToHarvest(source) || harvestBusy}
            title={
              !readyToHarvest(source)
                ? inService
                  ? t('connections.verifyFirst')
                  : t('connectionDetail.harvestOutOfService')
                : harvestBusy
                  ? t('connectionDetail.harvestInFlight')
                  : undefined
            }
            onClick={() => {
              harvest.mutate(source.id, {
                onSuccess: (operation) => {
                  setWatching(operation.id);
                },
              });
            }}
          >
            {t('connectionDetail.harvestNow')}
          </Button>
          {/* Beside the control, because the control's own `title` already carries why it may be
              disabled. */}
          <InfoTip
            subject={t('connectionDetail.harvestNow')}
            text={t('help.feature.harvest')}
          />
          <OverflowMenu label={t('connectionDetail.moreActions')} items={menuItems} />
        </div>
      </div>

      {/* **The banner for a failed source says what the platform actually knows.** A `failed` state
          records that a verification was refused; the driver's reason is deliberately not persisted —
          a connect error can carry the constructed DSN — so the banner says it was refused and offers
          the fix, and verifying again is what reports why. */}
      {source.state === 'failed' ? (
        <div className="lq-cd__banner" data-tone="error" role="alert">
          <strong className="lq-cd__bannerTitle">
            {t('connectionDetail.failedTitle')}
          </strong>
          <span className="lq-cd__bannerText">
            {checked.kind === 'never'
              ? t('connectionDetail.failedNoDate')
              : t('connectionDetail.failedBody')}
          </span>
          {mayRotate ? (
            <Button
              size="compact"
              onClick={() => {
                setEditing(true);
              }}
            >
              {t('connectionDetail.credential.open')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {source.state === 'registered' ? (
        <div className="lq-cd__banner" data-tone="info">
          <span className="lq-cd__bannerText">
            <strong className="lq-cd__bannerTitle">
              {t('connectionDetail.neverTitle')}
            </strong>{' '}
            {t('connectionDetail.neverBody')}
          </span>
          <Button
            size="compact"
            busy={verify.isPending}
            onClick={() => {
              verify.mutate(source.id);
            }}
          >
            {t('connections.verify')}
          </Button>
        </div>
      ) : null}

      {!inService ? (
        <div className="lq-cd__banner" data-tone="neutral">
          <span className="lq-cd__bannerText">
            <strong className="lq-cd__bannerTitle">
              {t('connectionDetail.disabledTitle')}
            </strong>{' '}
            {t('connectionDetail.disabledBody')}
          </span>
        </div>
      ) : null}

      {/* The verification's own answer, which is the only place a reason for a refusal exists. */}
      {unreachable !== null ? (
        <Alert tone="error" title={t('connections.unreachable')}>
          {unreachable.detail}
        </Alert>
      ) : null}
      {verify.data?.reachable === true ? (
        <Alert tone="notice" title={t('connectionDetail.verifiedTitle')}>
          {t('connectionDetail.verifiedBody')}
        </Alert>
      ) : null}
      {actionFailure !== null && blocking === null ? (
        <Alert tone="error" title={t('connections.actionFailed')}>
          {actionFailure}
        </Alert>
      ) : null}

      {watching !== null ? (
        <HarvestProgress
          harvestId={watching}
          onDismiss={() => {
            setDismissed(watching);
            setWatching(null);
          }}
          onFinished={onHarvestFinished}
        />
      ) : null}

      <div
        className="lq-cd__tiles"
        role="region"
        aria-label={t('connectionDetail.figures')}
      >
        {tiles.map((tile) => (
          <div key={tile.id} className="lq-cd__figure" data-tone={tile.tone}>
            <span className="lq-cd__figureMark" aria-hidden="true">
              <span className="lq-cd__figureFill" />
            </span>
            <div className="lq-cd__figureText">
              <HelpLabel subject={tile.label} help={tile.help}>
                <div className="lq-cd__figureLabel">{tile.label}</div>
              </HelpLabel>
              <div className="lq-cd__figureValue">{tile.value}</div>
              {tile.sub === '' ? null : (
                <div className="lq-cd__figureSub">{tile.sub}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div>
        <Tabs
          label={t('connectionDetail.sections')}
          active={tab}
          onSelect={setTab}
          tabs={[
            {
              id: 'datasets',
              label: t('connectionDetail.tab.datasets'),
              count: datasetCount ?? undefined,
            },
            { id: 'automation', label: t('connectionDetail.tab.automation') },
            { id: 'activity', label: t('connectionDetail.tab.activity') },
            { id: 'details', label: t('connectionDetail.tab.details') },
          ]}
        />

        {tab === 'datasets' ? (
          <div className="lq-cd__panel" role="tabpanel" data-lq-panel="">
            {running.kind === 'running' ? (
              <p className="lq-source__note">
                {t('connections.card.objectsRead', {
                  observed: running.observed,
                  discovered: running.discovered,
                })}
              </p>
            ) : null}
            {/* `key` forces a clean remount between two sources, so a selection or a search made on
                one source's tables is never carried onto another's. */}
            <SourceDatasetsTab
              key={connectionId}
              connectionId={connectionId}
              connectionName={source.name}
              inService={inService}
              mayProfile={can(identity.data, PROFILE_DATASETS_PERMISSION)}
              mayClassify={can(identity.data, CLASSIFY_DATASETS_PERMISSION)}
            />
          </div>
        ) : null}

        {tab === 'automation' ? (
          <div
            className="lq-cd__panel lq-cd__panel--grid"
            role="tabpanel"
            data-lq-panel=""
          >
            {/* A cadence and automatic profiling are standing instructions about this source, in the
                order somebody decides them: how often to look, then whether looking also measures. */}
            <HarvestScheduleCard
              connectionId={connectionId}
              durable={durable}
              mayConfigure={can(identity.data, CONFIGURE_HARVEST_SCHEDULE_PERMISSION)}
            />
            <AutoProfileCard
              connectionId={connectionId}
              mayChange={can(identity.data, PROFILE_DATASETS_PERMISSION)}
            />
          </div>
        ) : null}

        {tab === 'activity' ? (
          <div className="lq-cd__panel" role="tabpanel" data-lq-panel="">
            <SourceActivity connectionId={source.id} now={now} />
          </div>
        ) : null}

        {tab === 'details' ? (
          <div
            className="lq-cd__panel lq-cd__panel--details"
            role="tabpanel"
            data-lq-panel=""
          >
            <section className="lq-cd__box" aria-labelledby="lq-cd-connection">
              <h2 className="lq-cd__boxTitle" id="lq-cd-connection">
                {t('connectionDetail.connection')}
              </h2>
              <dl className="lq-cd__facts">
                <dt>{t('connectionDetail.fact.connector')}</dt>
                <dd>{CONNECTOR_LABEL[source.connector_type]}</dd>
                <dt>{t('connectionDetail.fact.version')}</dt>
                <dd className="lq-cd__version" data-support={version.kind}>
                  {versionText(version) ?? '—'}
                  <span className="lq-cd__versionNote">
                    {versionSentence(t, version, CONNECTOR_LABEL[source.connector_type])}
                  </span>
                </dd>
                {/* A source reached by account or workspace name has no host and port worth
                    separating, so it reads as one line, as the design has it. */}
                {profile?.needs_endpoint === false ? (
                  <>
                    <dt>{t('connectionDetail.fact.accountHost')}</dt>
                    <dd>{source.host}</dd>
                  </>
                ) : (
                  <>
                    <dt>{t('connectionDetail.fact.host')}</dt>
                    <dd>{source.host}</dd>
                    <dt>{t('connectionDetail.fact.port')}</dt>
                    <dd>{String(source.port)}</dd>
                  </>
                )}
                <dt>{t('connectionDetail.fact.database')}</dt>
                <dd>{source.database}</dd>
                <dt>{t('connectionDetail.fact.credential')}</dt>
                {/* Never the credential itself — nothing in the API can carry it. The path to the
                    secret store entry stays on hover, because pointing at the wrong entry is the
                    commonest registration mistake and an operator needs some way to check it. */}
                <dd title={credentials.path}>
                  {t('connectionDetail.fact.credentialStored')}
                </dd>
                {/* Only a declared region is a fact worth a row. An undeclared or unread one is
                    left out rather than shown as a claim about the tenant's configuration. */}
                {placements.isSuccess && region !== null ? (
                  <>
                    <dt>{t('connectionDetail.fact.region')}</dt>
                    <dd>{region}</dd>
                  </>
                ) : null}
                <dt>{t('connectionDetail.fact.lastVerified')}</dt>
                <dd>
                  {checked.kind === 'never'
                    ? t('connectionDetail.fact.never')
                    : checkedSentence(t, checked)}
                </dd>
              </dl>
              {mayRotate ? (
                <div className="lq-cd__boxAction">
                  <Button
                    size="compact"
                    onClick={() => {
                      setEditing(true);
                    }}
                  >
                    {t('connectionDetail.credential.open')}
                  </Button>
                </div>
              ) : null}
            </section>

            <section className="lq-cd__box" aria-labelledby="lq-cd-can">
              <HelpLabel
                subject={t('connectionDetail.can.title')}
                help={t('help.term.connectorCapability')}
              >
                <h2 className="lq-cd__boxTitle" id="lq-cd-can">
                  {t('connectionDetail.can.title')}
                </h2>
              </HelpLabel>
              <ul className="lq-cd__caps">
                {CAPABILITIES.map((capability) => {
                  const answer = support[capability];
                  return (
                    <li
                      key={capability}
                      className="lq-cd__cap"
                      /* The row says what Lumiq can do; why it cannot is on hover, for whoever
                         asks. */
                      title={t(`connectionDetail.capability.${capability}.${answer}`)}
                    >
                      <span
                        className="lq-cd__capMark"
                        data-answer={answer}
                        aria-hidden="true"
                      >
                        {answer === 'yes'
                          ? '✓'
                          : answer === 'no'
                            ? '—'
                            : answer === 'limited'
                              ? '!'
                              : '?'}
                      </span>
                      <span>
                        {t(`connectionDetail.can.${capability}`)}
                        <span className="lq-visually-hidden">
                          {' '}
                          {t(`connectionDetail.can.answer.${answer}`)}
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ul>
              {reach === 'unknown' ? (
                <p className="lq-cd__boxNote">{t('connectionDetail.can.unread')}</p>
              ) : null}
              {!tracksRenames(source) ? (
                <p className="lq-cd__boxNote">{t('connectionDetail.can.renames')}</p>
              ) : null}
            </section>

            <section className="lq-cd__box" aria-labelledby="lq-cd-schemas">
              <h2 className="lq-cd__boxTitle" id="lq-cd-schemas">
                {t('connectionDetail.schemas.title')}
              </h2>
              {sourceDatasets.isPending ? (
                <SkeletonRows rows={3} columns={2} />
              ) : sourceDatasets.isError ? (
                <p className="lq-cd__boxNote">{t('connectionDetail.schemas.unread')}</p>
              ) : sourceDatasets.data.rows.length > 0 ? (
                <ul className="lq-cd__schemas">
                  {schemasOf(sourceDatasets.data.rows).map((schema) => (
                    <li key={schema.name} className="lq-cd__schema">
                      <span className="lq-cd__schemaName">{schema.name}</span>
                      <span className="lq-cd__schemaCount">
                        {t('connectionDetail.schemas.count', { count: schema.datasets })}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : schemaPreview.isSuccess ? (
                schemaPreview.data.items.length === 0 ? (
                  <p className="lq-cd__boxNote">{t('connectionDetail.schemas.empty')}</p>
                ) : (
                  <ul className="lq-cd__schemas">
                    {schemaPreview.data.items.map((schema) => (
                      <li key={schema.name} className="lq-cd__schema">
                        <span className="lq-cd__schemaName">{schema.name}</span>
                        <span className="lq-cd__schemaCount">
                          {t('connectionDetail.schemas.count', { count: schema.objects })}
                        </span>
                      </li>
                    ))}
                  </ul>
                )
              ) : (
                <>
                  <p className="lq-cd__boxNote">
                    {schemaPreview.isError
                      ? t('connectionDetail.schemas.readFailed')
                      : t('connectionDetail.schemas.none')}
                  </p>
                  {inService ? (
                    <div className="lq-cd__boxAction">
                      <Button
                        size="compact"
                        busy={schemaPreview.isFetching}
                        onClick={() => {
                          if (askedSchemas) void schemaPreview.refetch();
                          else setAskedSchemas(true);
                        }}
                      >
                        {t('connectionDetail.schemas.read')}
                      </Button>
                    </div>
                  ) : null}
                </>
              )}
            </section>
          </div>
        ) : null}
      </div>

      <ReplaceCredentialDialog
        open={editing}
        connection={source}
        profile={profile}
        busy={credential.isPending}
        error={credential.error?.message}
        onSubmit={(values) => {
          credential.mutate(
            { connectionId: source.id, credentials: values },
            {
              onSuccess: () => {
                setEditing(false);
                /* Reset once the dialog has closed, so no submitted credential is left on the
                   mutation observer's `variables`. */
                credential.reset();
              },
            },
          );
        }}
        onClose={() => {
          setEditing(false);
          credential.reset();
        }}
      />

      {/* **Two presses when the source has datasets, and the second one carries a number.** The
          server refuses a plain delete of a source with datasets and states how many, so the dialog
          can then offer to retire the source together with exactly that many — the fresh count is
          the only one reachable. */}
      <ConfirmDialog
        open={armed}
        title={t('connections.deleteTitle')}
        message={
          <>
            <p className="lq-cd__confirmText">
              {blocking === null
                ? t('connections.deleteAsk', { name: source.name })
                : t('connections.deleteWithDatasetsAsk', {
                    name: source.name,
                    datasets: blocking,
                  })}
            </p>
            <TextField
              label={t('connectionDetail.typeName', { name: source.name })}
              value={typedName}
              onChange={setTypedName}
              mono
              autoComplete="off"
            />
          </>
        }
        confirmLabel={
          blocking === null
            ? t('connections.delete')
            : t('connections.deleteWithDatasets', { datasets: blocking })
        }
        cancelLabel={t('common.cancel')}
        tone="danger"
        busy={forget.isPending}
        /* The design's rule: delete asks for the source's name, typed. A misclick on the wrong
           source's menu then cannot retire it. */
        confirmDisabled={typedName.trim() !== source.name}
        error={forget.isError && blocking === null ? forget.error.message : undefined}
        onConfirm={() => {
          forget.mutate(
            blocking === null
              ? { connectionId: source.id }
              : { connectionId: source.id, retireDatasets: blocking },
            {
              /* **Back to the list, which is where the confirmation can live.** This page is about a
                 source that no longer exists, so staying on it could only say "not found". `replace`
                 so Back from the list does not return here either. */
              onSuccess: () => {
                setArmed(false);
                navigate(SCREEN_PATHS.connections, {
                  replace: true,
                  state: departureState({ name: source.name, retired: blocking ?? 0 }),
                });
              },
            },
          );
        }}
        onCancel={() => {
          setArmed(false);
          setTypedName('');
          forget.reset();
        }}
      />

      <ConfirmDialog
        open={askingService}
        title={
          inService
            ? t('connectionDetail.serviceOff.title', { name: source.name })
            : t('connectionDetail.serviceOn.title', { name: source.name })
        }
        message={
          inService
            ? t('connectionDetail.serviceOff.body')
            : t('connectionDetail.serviceOn.body')
        }
        confirmLabel={
          inService ? t('connectionDetail.disable') : t('connectionDetail.enable')
        }
        cancelLabel={t('common.cancel')}
        tone={inService ? 'danger' : 'primary'}
        busy={service.isPending}
        error={service.isError ? service.error.message : undefined}
        onConfirm={() => {
          service.mutate(
            { connectionId: source.id, inService: !inService },
            {
              onSuccess: () => {
                setAskingService(false);
              },
            },
          );
        }}
        onCancel={() => {
          setAskingService(false);
          service.reset();
        }}
      />
    </Sections>
  );
}

/* i18next's own function type rather than a hand-written signature. A narrower local alias
 * type-checks at the definition and not at the call site — `t` carries overloads for the
 * `defaultValue` forms this file uses, and a simplified alias silently drops them under
 * `exactOptionalPropertyTypes`. */
type Translate = TFunction;

/* The last-checked sentence, in the card's own voice rather than the table's compact one.
 *
 * The table says "41 d ago" because it is scanned; the card says "41 days ago" because it is read.
 * One reading, two renderings, and the reading is `lastChecked` in both — a second bucketing would let
 * the two disagree about when an hour becomes hours.
 */
function checkedSentence(t: Translate, checked: ReturnType<typeof lastChecked>): string {
  switch (checked.kind) {
    case 'never':
      return t('connectionDetail.checked.never');
    case 'justNow':
      return t('connectionDetail.checked.justNow');
    case 'minutes':
      return t('connectionDetail.checked.minutes', { count: checked.count });
    case 'hours':
      return t('connectionDetail.checked.hours', { count: checked.count });
    default:
      return t('connectionDetail.checked.days', { count: checked.count });
  }
}
